import { memo, useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { RatingStars } from "@/components/RatingStars";

const makeIcon = (active) =>
  L.divIcon({
    className: `facility-marker ${active ? "facility-marker-active" : ""}`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });

const US_CENTER = [39.5, -98.35];

const FacilityMarker = memo(({ facility, active, onSelect }) => {
  const position = useMemo(() => [facility.latitude, facility.longitude], [facility]);
  const icon = useMemo(() => makeIcon(active), [active]);
  const eventHandlers = useMemo(() => ({ click: () => onSelect(facility) }), [facility, onSelect]);

  return (
    <Marker
      data-testid={`facility-map-marker-${facility.id}`}
      position={position}
      icon={icon}
      eventHandlers={eventHandlers}
    >
      <Popup>
        <div data-testid={`facility-map-popup-${facility.id}`} className="w-64 space-y-2">
          <p data-testid={`facility-map-popup-city-${facility.id}`} className="text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">
            {facility.city}, {facility.state}
          </p>
          <h3 data-testid={`facility-map-popup-name-${facility.id}`} className="font-display text-lg font-black leading-tight tracking-normal text-[#09090B]">
            {facility.name}
          </h3>
          <div data-testid={`facility-map-popup-rating-${facility.id}`} className="flex items-center gap-2 text-sm font-bold">
            <RatingStars value={facility.rating_average} testId={`facility-map-popup-stars-${facility.id}`} />
            {facility.rating_average}
          </div>
          <Link data-testid={`facility-map-popup-link-${facility.id}`} to={`/facility/${facility.id}`} className="font-bold text-[#0055FF]">
            View facility
          </Link>
        </div>
      </Popup>
    </Marker>
  );
});

FacilityMarker.displayName = "FacilityMarker";

const MapFocus = ({ facility }) => {
  const map = useMap();

  useEffect(() => {
    if (facility) {
      map.flyTo([facility.latitude, facility.longitude], 7, { duration: 0.85 });
    }
  }, [facility, map]);

  return null;
};

export const FacilityMap = ({ facilities, activeFacility, onSelect }) => {
  return (
    <div data-testid="facility-map-panel" className="h-[58vh] min-h-[460px] overflow-hidden border border-zinc-200 bg-zinc-100 md:h-[80vh]">
      <MapContainer
        data-testid="facility-map"
        center={US_CENTER}
        zoom={4}
        minZoom={3}
        scrollWheelZoom
        className="h-full w-full"
      >
        <TileLayer
          data-testid="facility-map-tiles"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapFocus facility={activeFacility} />
        {facilities.map((facility) => (
          <FacilityMarker
            key={facility.id}
            facility={facility}
            active={activeFacility?.id === facility.id}
            onSelect={onSelect}
          />
        ))}
      </MapContainer>
    </div>
  );
};